/**
 * O manifesto selado de um recebimento no recibo da Custódia (E135.F2, GUS-158, ADR-0091).
 *
 * O manifesto é o inventário da plataforma, em JSON canônico, com o SHA-256 dos bytes. A tela
 * mostra o ID, o SHA-256, quando foi selado, de qual manifesto ele é complemento e cada arquivo
 * que chegou: inclusive a cópia de arquivo já recebido e o arquivo do sistema operacional, que
 * não é lido. Sem assinatura nem carimbo do tempo (nenhum perfil aprovado), a tela diz isso em
 * vez de alegar uma âncora que não existe. "Baixar manifesto" entrega os bytes exatos, para a
 * conferência por fora.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Badge, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  hashCurto,
  idCurto,
  type OcorrenciaNoReciboData,
  type RecebimentoNoReciboData,
} from '@/lib/abertura_do_caso';
import { salvarArquivoBaixado } from '@/lib/http_download';
import { baixarManifesto } from '@/lib/recebimentos';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.abertura.custodia.manifesto;

function BaixarManifesto({
  client,
  caseId,
  recebimentoId,
}: {
  client: AxiosInstance;
  caseId: string;
  recebimentoId: string;
}): JSX.Element {
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState(false);
  async function baixar(): Promise<void> {
    setBaixando(true);
    setErro(false);
    try {
      salvarArquivoBaixado(await baixarManifesto(client, caseId, recebimentoId));
    } catch {
      setErro(true);
    } finally {
      setBaixando(false);
    }
  }
  return (
    <span className="flex flex-wrap items-center gap-sc-2">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={baixando}
        data-testid="custodia-baixar-manifesto"
        onClick={() => void baixar()}
      >
        {baixando ? t.baixando : t.baixar}
      </Button>
      {erro ? (
        <span role="alert" className="text-caption text-danger-700">
          {t.erroBaixar}
        </span>
      ) : null}
    </span>
  );
}

function LinhaDaOcorrencia({ ocorrencia }: { ocorrencia: OcorrenciaNoReciboData }): JSX.Element {
  return (
    <li
      data-testid="custodia-ocorrencia"
      className="flex flex-wrap justify-between gap-sc-2 py-0.5"
    >
      <span className="min-w-0 truncate text-caption text-fg">{ocorrencia.caminho}</span>
      <span className="text-caption text-fg-muted">
        {t.situacao[ocorrencia.situacao]} ·{' '}
        <span className="font-mono" title={ocorrencia.sha256 ?? undefined}>
          {hashCurto(ocorrencia.sha256) ?? t.naoLido}
        </span>
      </span>
    </li>
  );
}

export function ManifestoDoRecebimento({
  client,
  caseId,
  recebimento,
}: {
  client: AxiosInstance;
  caseId: string;
  recebimento: RecebimentoNoReciboData;
}): JSX.Element | null {
  const id = recebimento.recebimento_id;
  if (id === null) return null;
  const manifesto = recebimento.manifesto;
  const ocorrencias = recebimento.ocorrencias ?? [];
  if (manifesto === null || manifesto === undefined) {
    return (
      <p className="mt-1 text-caption text-fg-muted" data-testid="custodia-manifesto-aberto">
        {t.aberto}
      </p>
    );
  }
  return (
    <div className="mt-sc-2 flex flex-col gap-1" data-testid="custodia-manifesto">
      <p className="text-caption text-fg">
        <span title={manifesto.manifesto_id}>
          {t.linha
            .replace('{id}', idCurto(manifesto.manifesto_id))
            .replace('{data}', formatarDataHora(manifesto.selado_em))}
        </span>
        {' · SHA-256 '}
        <span className="font-mono" title={manifesto.sha256} data-testid="custodia-manifesto-hash">
          {hashCurto(manifesto.sha256)}
        </span>
      </p>
      {manifesto.anterior_id !== null ? (
        <p className="text-caption text-fg-muted">
          {t.complemento.replace('{id}', idCurto(manifesto.anterior_id))}
        </p>
      ) : null}
      <p className="text-caption text-fg-muted">
        <Badge variant="neutral">{t.semAncora}</Badge>
      </p>
      <BaixarManifesto client={client} caseId={caseId} recebimentoId={id} />
      {ocorrencias.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-caption text-fg-muted">
            {t.arquivos.replace('{n}', String(ocorrencias.length))}
          </summary>
          <ul className="ml-sc-4 border-l border-border pl-sc-4">
            {ocorrencias.map((o) => (
              <LinhaDaOcorrencia key={o.ocorrencia_id} ocorrencia={o} />
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

export function ComoConferir(): JSX.Element {
  return (
    <details className="mt-sc-3" data-testid="custodia-como-conferir">
      <summary className="cursor-pointer text-body-sm font-semibold text-fg">
        {t.comoConferirTitulo}
      </summary>
      <ol className="ml-sc-4 mt-1 list-decimal text-caption text-fg-muted">
        {t.comoConferirPassos.map((passo) => (
          <li key={passo}>{passo}</li>
        ))}
      </ol>
    </details>
  );
}
